import { useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  PaymentMethods,
} from 'types/beneficiaries'
import { DEFAULT_FIAT_INSTRUMENT } from '../constants'
import {
  ContactFiatInstrumentFormData,
  contactFiatInstrumentSchema,
} from '../schemas/contactFiatInstrumentSchema'
import { FormFieldLabels, Step, Steps } from '../types/fiatInstruments'

export const useFiatInstrumentForm = () => {
  const { t } = useTranslation()

  const [step, setStep] = useState<Step>(Steps.CURRENCY_DETAILS)

  const formSchema = contactFiatInstrumentSchema(t)

  const form = useForm<ContactFiatInstrumentFormData>({
    defaultValues: DEFAULT_FIAT_INSTRUMENT,
    resolver: yupResolver(formSchema),
  })

  const { control, formState, clearErrors, resetField, setValue, register, trigger, setError } =
    form

  const formValues = useWatch({ control }) as ContactFiatInstrumentFormData

  const isInternationalBankAccount = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isLocalBankAccount = !isInternationalBankAccount && !!formValues?.paymentMethods?.length
  const isBusinessBankAccount = formValues?.entityType === BeneficiaryEntityType.BUSINESS

  const formFieldLabels: FormFieldLabels = useMemo(() => {
    if (isInternationalBankAccount) {
      return {
        accountNumber: t('formFields.accountNumberOrIban'),
        accountNumberPlaceholder: t('formFields.placeholder.accountNumberOrIban'),
        bankCode: t('formFields.swiftBankCode'),
        bankCodePlaceholder: t('formFields.placeholder.swiftBankCode'),
      }
    }
    if (isLocalBankAccount) {
      switch (formValues?.currency) {
        case BeneficiaryDetailsCurrency.EUR:
          return {
            accountNumber: t('formFields.iban'),
            accountNumberPlaceholder: t('formFields.placeholder.iban'),
            bankCode: t('formFields.bic'),
            bankCodePlaceholder: t('formFields.placeholder.bic'),
          }
        case BeneficiaryDetailsCurrency.GBP:
          return {
            accountNumber: t('formFields.accountNumber'),
            accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
            bankCode: t('formFields.sortCode'),
            bankCodePlaceholder: t('formFields.placeholder.sortCode'),
          }
        case BeneficiaryDetailsCurrency.USD:
          return {
            accountNumber: t('formFields.accountNumber'),
            accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
            bankCode: t('formFields.routingNumber'),
            bankCodePlaceholder: t('formFields.placeholder.routingNumber'),
          }
        default:
          return {
            accountNumber: t('formFields.accountNumber'),
            accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
            bankCode: t('formFields.bankCode'),
            bankCodePlaceholder: t('formFields.placeholder.bankCode'),
          }
      }
    }
  }, [formValues?.currency, formValues?.paymentMethods])

  return {
    // steps
    step,
    setStep,
    // form
    formValues,
    formState,
    isInternationalBankAccount,
    isBusinessBankAccount,
    formFieldLabels,
    errors: formState.errors,
    clearErrors,
    resetField,
    setValue,
    register,
    setError,
    trigger,
    form,
  }
}
